import { useEffect, useRef, useState } from 'react'
import { Reorder } from 'framer-motion'
import { Check, GripVertical, MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSaveInboxFilters, type InboxFilter } from '@/api/inbox'

interface Props {
  filter: InboxFilter
  onFilter: (f: InboxFilter) => void
  /** Épinglés, dans l'ordre de l'utilisateur ; « all » en tête. */
  pinned: InboxFilter[]
  /** Ce que le plan permet, dans l'ordre du menu. */
  available: InboxFilter[]
  labels: Record<InboxFilter, string>
  /** Compteurs affichés à côté d'un filtre (Non lus, Mentions). */
  counts: Partial<Record<InboxFilter, number>>
  darkMode: boolean
}

/**
 * La rangée de filtres de la boîte : seulement les épinglés, puis « ⋯ ».
 * Le menu liste tous les filtres du plan : cocher épingle, cliquer sur le
 * nom applique le filtre, glisser réordonne les épinglés. « Tous » reste.
 */
const FIXES: InboxFilter[] = ['all', 'open', 'done']

export function FiltresBoite({ filter, onFilter, pinned, available, labels, counts, darkMode }: Props) {
  const { t } = useTranslation()
  const sauver = useSaveInboxFilters()
  const [ouvert, setOuvert] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // « Tous », et en équipe « À traiter » et « Terminées », restent toujours dans la rangée :
  // le serveur les ajoute, ni glisser ni décocher.
  const fixes: InboxFilter[] = ['all', ...pinned.filter((f) => FIXES.includes(f) && f !== 'all')]
  // L'ordre suivi pendant le glisser ; le serveur ne reçoit que l'ordre final.
  const autres: InboxFilter[] = pinned.filter((f) => !FIXES.includes(f))
  const cle = autres.join(',')
  const [ordre, setOrdre] = useState(autres)
  const ordreRef = useRef(autres)
  const enGlisse = useRef(false)
  useEffect(() => {
    if (enGlisse.current) return
    const suivant = cle.split(',').filter(Boolean) as InboxFilter[]
    ordreRef.current = suivant
    setOrdre(suivant)
  }, [cle])

  useEffect(() => {
    if (!ouvert) return
    const fermer = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOuvert(false)
    }
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && setOuvert(false)
    document.addEventListener('mousedown', fermer)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('mousedown', fermer)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  const enregistrer = (suite: InboxFilter[]) => sauver.mutate(['all', ...suite])
  const basculer = (f: InboxFilter) =>
    enregistrer(ordreRef.current.includes(f) ? ordreRef.current.filter((x) => x !== f) : [...ordreRef.current, f])
  const lacher = () => {
    enGlisse.current = false
    if (ordreRef.current.join(',') !== cle) enregistrer(ordreRef.current)
  }

  // La rangée : les épinglés, plus le filtre choisi depuis le menu s'il ne l'est pas.
  const rangee: InboxFilter[] = [...fixes, ...autres]
  if (!rangee.includes(filter)) rangee.push(filter)
  const nonEpingles = available.filter((f) => !FIXES.includes(f) && !autres.includes(f))
  // Un compteur caché derrière « ⋯ » : une pastille pour ne rien rater.
  const cache = nonEpingles.some((f) => f !== filter && (counts[f] ?? 0) > 0)

  const badge = (f: InboxFilter, actif: boolean) => {
    const n = counts[f] ?? 0
    if (n <= 0) return null
    return (
      <span
        className={cn(
          'ml-1 inline-flex items-center justify-center min-w-3.5 h-3.5 px-0.5 rounded-full text-[8px] font-bold',
          actif
            ? f === 'mentions'
              ? 'bg-amber-500 text-white'
              : 'bg-indigo-500 text-white'
            : darkMode
              ? 'bg-slate-700 text-slate-300'
              : 'bg-gray-200 text-gray-600',
        )}
      >
        {n > 99 ? '99+' : n}
      </span>
    )
  }

  const ligneMenu = (f: InboxFilter, epingle: boolean, glissable: boolean) => (
    <div className="flex items-center gap-1.5 px-2 py-1">
      <span
        className={cn('w-3.5 flex-shrink-0', glissable && 'cursor-grab active:cursor-grabbing', darkMode ? 'text-slate-600' : 'text-slate-300')}
        title={glissable ? t('inbox.filtersMenu.drag', 'Glisser pour déplacer') : undefined}
      >
        {glissable && <GripVertical className="h-3.5 w-3.5" />}
      </span>
      <button
        type="button"
        role="checkbox"
        aria-checked={epingle}
        aria-label={labels[f]}
        disabled={FIXES.includes(f)}
        onClick={() => basculer(f)}
        className={cn(
          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-50',
          epingle
            ? 'bg-indigo-600 border-indigo-600 text-white'
            : darkMode
              ? 'border-slate-600 hover:border-slate-400'
              : 'border-slate-300 hover:border-slate-500',
        )}
      >
        {epingle && <Check className="h-3 w-3" />}
      </button>
      <button
        type="button"
        onClick={() => {
          onFilter(f)
          setOuvert(false)
        }}
        className={cn(
          'flex-1 min-w-0 flex items-center text-left text-[12px] truncate',
          filter === f ? (darkMode ? 'text-indigo-300 font-medium' : 'text-indigo-700 font-medium') : darkMode ? 'text-slate-300' : 'text-slate-700',
        )}
      >
        <span className="truncate">{labels[f]}</span>
        {badge(f, false)}
      </button>
    </div>
  )

  return (
    <div className="relative mt-2 flex items-center gap-1">
      {/* Les filtres défilent de côté ; le fondu à droite montre qu'il y en a d'autres. */}
      <div className="relative flex-1 min-w-0">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pr-6">
          {rangee.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onFilter(key)}
              className={cn(
                'px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap transition-all flex items-center flex-shrink-0',
                filter === key
                  ? key === 'missed'
                    ? darkMode
                      ? 'bg-red-500/15 text-red-400'
                      : 'bg-red-50 text-red-700'
                    : darkMode
                      ? 'bg-indigo-500/20 text-indigo-300'
                      : 'bg-indigo-50 text-indigo-700'
                  : darkMode
                    ? 'text-slate-500 hover:text-slate-300'
                    : 'text-gray-400 hover:text-gray-600',
              )}
            >
              {labels[key]}
              {badge(key, filter === key)}
            </button>
          ))}
        </div>
        <div
          aria-hidden
          className={cn('pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l', darkMode ? 'from-slate-950' : 'from-white')}
        />
      </div>

      <div ref={menuRef} className="relative flex-shrink-0">
        <button
          type="button"
          onClick={() => setOuvert((o) => !o)}
          title={t('inbox.filtersMenu.title', 'Filtres affichés')}
          aria-expanded={ouvert}
          className={cn(
            'relative w-6 h-5 rounded flex items-center justify-center transition-colors',
            ouvert
              ? darkMode
                ? 'bg-slate-800 text-white'
                : 'bg-gray-100 text-gray-700'
              : darkMode
                ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
          )}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
          {cache && <span className="absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-indigo-500" />}
        </button>

        {ouvert && (
          <div
            className={cn(
              'absolute right-0 top-full mt-1 z-50 w-56 rounded-lg border py-1 shadow-lg',
              darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200',
            )}
          >
            <p className={cn('px-3 pt-1 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider', darkMode ? 'text-slate-500' : 'text-slate-400')}>
              {t('inbox.filtersMenu.title', 'Filtres affichés')}
            </p>
            {fixes.map((f) => (
              <div key={f}>{ligneMenu(f, true, false)}</div>
            ))}
            <Reorder.Group
              as="div"
              axis="y"
              values={ordre}
              onReorder={(suivant: InboxFilter[]) => {
                ordreRef.current = suivant
                setOrdre(suivant)
              }}
            >
              {ordre.map((f) => (
                <Reorder.Item
                  key={f}
                  value={f}
                  as="div"
                  onDragStart={() => (enGlisse.current = true)}
                  onDragEnd={lacher}
                  className={cn('rounded', darkMode ? 'bg-slate-900' : 'bg-white')}
                >
                  {ligneMenu(f, true, true)}
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {nonEpingles.length > 0 && (
              <div className={cn('mt-1 pt-1 border-t', darkMode ? 'border-slate-800' : 'border-slate-100')}>
                {nonEpingles.map((f) => (
                  <div key={f}>{ligneMenu(f, false, false)}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
